'use strict';

/**
 * OIDC callback parameters Keycloak appends to the redirect URL. They can sit in the query, in
 * the fragment, or in the path when AngularJS html5Mode rewrites `/#state=...` into `/state=...`,
 * and they end up in log messages too.
 *
 * @module monitoring.authParamsSanitize
 */

const AUTH_PARAM_DETECT_PATTERN = /[?&#/](?:code|state|session_state|iss)=/;
const AUTH_PARAM_PATTERN = /([?&#/])(?:code|state|session_state|iss)=[^&#\s"'\\<>]*/g;
const REMOVED_PARAM_PATTERN = /\uFFFF/g;
const REMOVED_PARAM_WITH_NEXT_SEPARATOR_PATTERN = /\uFFFF&/g;
const REMOVED_PARAM_WITH_OWN_SEPARATOR_PATTERN = /[?&#]\uFFFF/g;

/**
 * @param {string} value
 * @returns {string}
 */
function stripAuthParams(value) {
  if (!AUTH_PARAM_DETECT_PATTERN.test(value)) {
    return value;
  }

  // Each removed parameter leaves its separator followed by a marker, so only the separators
  // it owned are dropped: `?a=1&code=x&b=2` keeps `?a=1&b=2`, `/state=x&code=y` keeps `/`.
  return value
    .replace(AUTH_PARAM_PATTERN, '$1\uFFFF')
    .replace(REMOVED_PARAM_WITH_NEXT_SEPARATOR_PATTERN, '')
    .replace(REMOVED_PARAM_WITH_OWN_SEPARATOR_PATTERN, '')
    .replace(REMOVED_PARAM_PATTERN, '');
}

function isPlainObject(value) {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);

  return prototype === Object.prototype || prototype === null;
}

/**
 * @param {*} value
 * @returns {*} a copy of `value` with every string of its plain objects and arrays stripped
 */
function stripAuthParamsDeep(value) {
  if (typeof value === 'string') {
    return stripAuthParams(value);
  }

  if (Array.isArray(value)) {
    return value.map(stripAuthParamsDeep);
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const stripped = {};

  Object.keys(value).forEach(function (key) {
    stripped[key] = stripAuthParamsDeep(value[key]);
  });

  return stripped;
}

/**
 * Sentry `beforeSend`. `sdkProcessingMetadata` holds SDK internals (scopes, raw request) that are
 * never sent.
 */
function stripAuthParamsFromEvent(event) {
  const stripped = stripAuthParamsDeep(Object.assign({}, event, { sdkProcessingMetadata: undefined }));

  stripped.sdkProcessingMetadata = event.sdkProcessingMetadata;

  return stripped;
}

export { stripAuthParams, stripAuthParamsDeep, stripAuthParamsFromEvent };
